<template lang="pug">
.financial-detail-chart.cards(ref="chartContainerRef")
  .title {{ $t('financial.chart.title') }}
  svg(ref="chartRef" class="financialChart")
  .legend
    .legend-item(v-for="item in legendItems")
      .color(:class="item.colorClass")
      .name {{ $t('financial.chart.' + item.id) }}
    
</template>

<script lang="ts">
import { useCostChart } from "@/helpers/costs-graph";
import { Loan, FinanceDetails } from "@/types";

import { PropType, computed, defineComponent, onMounted, ref, watch } from "vue";

export default defineComponent({
  props: {
    financeDetails: {
      type: Object as PropType<FinanceDetails>,
    },

    loan: {
      type: Object as PropType<Loan>,
    },
  },
  setup(props) {
    const chartRef = ref<SVGGElement>();
    const chartContainerRef = ref<HTMLElement>();

    const { renderChart } = useCostChart(chartRef);

    const financeDetails = computed(() => props.financeDetails);
    const loan = computed(() => props.loan);

    const legendItems = [
      {
        colorClass: "pay-off",
        id: "payOff",
      },
      {
        colorClass: "repayment",
        id: "repayment",
      },
      {
        colorClass: "annual-savings",
        id: "annualSavings",
      },
    ];

    onMounted(() =>
      renderChart(
        financeDetails.value?.investmentCosts || [],
        loan.value,
        financeDetails.value?.breakEvenPointYear
      )
    );

    watch([financeDetails, loan], ([finance, loan]) =>
      renderChart(finance?.investmentCosts || [], loan, finance?.breakEvenPointYear)
    );

    return { chartRef, chartContainerRef, legendItems };
  },
});
</script>

<style lang="scss" scoped>
.financial-detail-chart {
  background-color: $background-color-default;
  padding-right: 2.5rem;
  padding-bottom: 1rem;

  .title {
    text-transform: uppercase;
    padding: 1rem 1rem 0.25rem 1rem;
    font-weight: bolder;
    color: $primary-color;
  }

  :deep(.financialChart) {
    overflow: visible;

    .axisLabel {
      font-size: 0.8rem;
      font-weight: 600;
      z-index: 9999;
      color: $font-color-default;
    }

    .domain,
    .tick line {
      // grid lines

      stroke: $background-color-disabled;
    }
    .repayment-bar {
      fill: $chart-repayment-color;
    }
    .benefit-bar {
      fill: $chart-annual-savings-color;
    }
    .investment-pay-off-fill {
      fill: $chart-pay-off-color;
    }
    .investment-pay-off-dots {
      stroke-width: 3;
      fill: transparent;
      stroke: $primary-color-active;
      stroke-dasharray: 0, 5;
      stroke-linecap: round;
    }

    .break-even-line {
      stroke-width: 1;
      stroke: $font-color-default;
    }

    .break-even-point {
      fill: $primary-color-active;
    }

    .break-even-label {
      transform: translate(20px, 32px);
      font-weight: 600;
      font-size: 1rem;
      fill: $primary-color;
    }
  }

  .legend {
    display: flex;
    gap: 5rem;
    justify-content: center;
    width: 100%;
    .legend-item {
      display: flex;
      gap: 0.5rem;
      .color {
        width: 1rem;
        height: 1rem;
        &.pay-off {
          background-color: $chart-pay-off-color;
        }
        &.repayment {
          background-color: $chart-repayment-color;
        }

        &.annual-savings {
          background-color: $chart-annual-savings-color;
        }
      }
    }
  }
}

@media screen and (max-width: 1080px) {
  .financial-detail-chart {
    .legend {
      gap: 1rem;
    }
  }
}

@media screen and (max-width: 720px) {
  .financial-detail-chart {
    .legend {
      flex-direction: column;
      margin-left: 1rem;
      gap: 1rem;
    }
  }
}
</style>
